import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { formatCpf } from "@brazilian-utils/brazilian-utils";
import api from '../../services/api';
import editIcon from '../../assets/edit.png';
import profileIcon from '../../assets/profile.png';
import { usePopup } from '../common/Popup';
import { formatPhone } from '../../utils/formatters';
import { phone as validatePhone } from '../../utils/validation';

const Container = styled.div`
  position: relative;
  width: 100%;
  max-width: 800px;
  padding: 10px 20px;
  box-sizing: border-box;
  border-radius: 2px;
  background-color: #ffffff;
`;

const HeaderRow = styled.div`
  position: absolute;
  top: 4px;
  right: 20px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 8px;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: #f28c28;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 1rem;
  font-weight: 600;
  padding: 4px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
`;

const SaveButton = styled.button`
  background-color: #6ab8a8;
  color: white;
  font-size: 0.95rem;
  font-weight: 700;
  padding: 8px 20px;
  border: none;
  border-radius: 8px;
  box-shadow: 0px 3px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background-color: #999;
  color: white;
  font-size: 0.95rem;
  font-weight: 700;
  padding: 8px 20px;
  border: none;
  border-radius: 8px;
  box-shadow: 0px 3px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #777;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Title = styled.h2`
  text-align: left;
  font-weight: 800;
  font-size: 1.2rem;
  color: #f28c28;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  text-align: left;
  font-weight: 700;
  font-size: 1rem;
  color: #f28c28;
  margin: 24px 0 12px 0;
  padding-bottom: 6px;
  border-bottom: 1px solid #f1f1f1;
`;

const AvatarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  width: 100%;
`;

const Avatar = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 3px solid #002E3B;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #002E3B;
  overflow: hidden;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const UserName = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: #333;
`;

const CargoBadge = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #ffffff;
  background-color: #6ab8a8;
  padding: 6px 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(106, 184, 168, 0.4);
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding-bottom: 20px;
`;

const FormRow = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
`;

const FormItem = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 150px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 2px;
  font-weight: bold;
  font-size: 0.9rem;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  height: 35px;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 1rem;
  box-sizing: border-box;
  background-color: #ffffff;
  color: #495057;

  &:focus {
    outline: none;
    border-color: #6ab8a8;
    box-shadow: 0 0 0 2px rgba(106, 184, 168, 0.3);
  }
`;

const ErrorMessage = styled.div`
  color: #c0392b;
  font-size: 0.8rem;
  margin-top: 4px;
`;

const FileSection = styled.div`
  margin-top: 12px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
`;

const FileLabel = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #6AB8A8;
  margin-bottom: 10px;
`;

const FileContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background-color: white;
  border: 1px dashed #ccc;
  border-radius: 5px;
`;

const FileName = styled.span`
  color: #6AB8A8;
  font-size: 0.9rem;
`;

const DownloadButton = styled.button`
  background-color: #6ab8a8;
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  text-align: left;
  padding: 40px;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #6ab8a8;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const CARGO_INSTITUICAO = 2;
const CARGO_COORDENADOR_ESTAGIO = 3;
const CARGO_NEPS_GEPS = 4;

const displayCpf = (value) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return '';
  return formatCpf(digits);
};

const displayTelefone = (value) => {
  if (!value) return '';
  return formatPhone(value);
};

const ProfileUserInfo = ({ userData = null, editable = false }) => {
  const popup = usePopup();
  const [user, setUser] = useState(userData);
  const [instituicao, setInstituicao] = useState(null);
  const [termo, setTermo] = useState(null);
  const [cursos, setCursos] = useState([]);
  const [estabelecimento, setEstabelecimento] = useState(null);
  const [loading, setLoading] = useState(!userData);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ nome: '', telefone: '' });
  const [saving, setSaving] = useState(false);
  const [downloadingTermo, setDownloadingTermo] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        let baseUser = userData;
        if (!baseUser) {
          baseUser = await api.getCurrentUser();
        }
        setUser(baseUser);

        const isExternalUser = !!userData;

        if (baseUser?.cargoId === CARGO_INSTITUICAO) {
          try {
            const inst = isExternalUser
              ? await api.getUsuarioInstituicao(baseUser.id)
              : await api.getUserInstitution();
            setInstituicao(inst || null);
          } catch (err) {
            console.error('Erro ao carregar instituição:', err);
          }
          try {
            const termoData = isExternalUser
              ? await api.getUsuarioTermo(baseUser.id)
              : await api.getMeuTermo();
            setTermo(termoData || null);
          } catch (err) {
            console.error('Erro ao carregar termo de cooperação:', err);
            setTermo(null);
          }
        } else if (baseUser?.cargoId === CARGO_COORDENADOR_ESTAGIO) {
          try {
            const cursosData = isExternalUser
              ? await api.getUsuarioCursos(baseUser.id)
              : await api.getMyCursos();
            setCursos(Array.isArray(cursosData) ? cursosData : []);
          } catch (err) {
            console.error('Erro ao carregar cursos:', err);
          }
        } else if (baseUser?.cargoId === CARGO_NEPS_GEPS && baseUser?.estabelecimentoSaudeId) {
          try {
            const estab = await api.getEstabelecimentosSaudeId(baseUser.estabelecimentoSaudeId);
            setEstabelecimento(estab || null);
          } catch (err) {
            console.error('Erro ao carregar estabelecimento de saúde:', err);
          }
        }
      } catch (err) {
        console.error('Erro ao carregar dados do usuário:', err);
        setError('Erro ao carregar dados do usuário');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userData]);

  useEffect(() => {
    if (!isEditing) {
      setFormData({
        nome: user?.nome || '',
        telefone: displayTelefone(user?.telefone),
      });
    }
  }, [user, isEditing]);

  const handleEditToggle = () => {
    setFormData({
      nome: user?.nome || '',
      telefone: displayTelefone(user?.telefone),
    });
    setIsEditing((prev) => !prev);
  };

  const handleFieldChange = (field) => (event) => {
    const { value } = event.target;
    if (field === 'telefone') {
      setFormData((prev) => ({ ...prev, telefone: formatPhone(value) }));
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const telefoneErro = isEditing ? validatePhone(formData.telefone) : '';

  const handleDownloadTermo = async () => {
    if (!user?.id || !termo) return;
    try {
      setDownloadingTermo(true);
      await api.downloadTermoCooperacao(user.id);
    } catch (err) {
      console.error('Erro ao baixar termo:', err);
      await popup.alert({
        icon: 'warning',
        title: 'Erro ao baixar termo',
        message: err.message || 'Não foi possível baixar o termo de cooperação.',
      });
    } finally {
      setDownloadingTermo(false);
    }
  };

  const handleSave = async () => {
    const nomeLimpo = (formData.nome || '').trim();
    if (!nomeLimpo) {
      await popup.alert({
        icon: 'warning',
        title: 'Dados inválidos',
        message: 'O nome não pode ficar em branco.',
      });
      return;
    }

    const telefoneTrim = (formData.telefone || '').trim();
    const erroTelefone = validatePhone(telefoneTrim);
    if (erroTelefone) {
      await popup.alert({
        icon: 'warning',
        title: 'Dados inválidos',
        message: erroTelefone,
      });
      return;
    }

    const telefoneFormatado = telefoneTrim ? formatPhone(telefoneTrim) : '';

    try {
      setSaving(true);
      const atualizado = await api.updateMe({
        nome: nomeLimpo,
        telefone: telefoneFormatado,
      });
      setUser((prev) => ({
        ...(prev || {}),
        nome: atualizado.nome,
        telefone: atualizado.telefone,
      }));
      setIsEditing(false);
      await popup.alert({
        icon: 'success',
        message: 'Dados atualizados com sucesso!',
      });
    } catch (err) {
      console.error('Erro ao atualizar dados do usuário:', err);
      await popup.alert({
        icon: 'warning',
        title: 'Erro ao atualizar',
        message: err.message || 'Verifique os dados e tente novamente.',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Title>CARREGANDO DADOS DO USUÁRIO...</Title>
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      </Container>
    );
  }

  const renderInstituicao = () => {
    if (!instituicao) return null;
    return (
      <>
        <SectionTitle>Dados da Instituição de Ensino</SectionTitle>
        <FormRow>
          <FormItem>
            <Label htmlFor="nomeRegistroMEC">Nome de registro no MEC</Label>
            <Input
              type="text"
              id="nomeRegistroMEC"
              value={instituicao.nomeRegistroMEC || ''}
              readOnly
            />
          </FormItem>
          <FormItem>
            <Label htmlFor="sigla">Sigla</Label>
            <Input
              type="text"
              id="sigla"
              value={instituicao.sigla || ''}
              readOnly
            />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem>
            <Label htmlFor="razaoSocial">Razão Social</Label>
            <Input
              type="text"
              id="razaoSocial"
              value={instituicao.razaoSocial || ''}
              readOnly
            />
          </FormItem>
          <FormItem>
            <Label htmlFor="cnpj">CNPJ</Label>
            <Input
              type="text"
              id="cnpj"
              value={instituicao.cnpj || ''}
              readOnly
            />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem>
            <Label htmlFor="instTelefone">Telefone</Label>
            <Input
              type="text"
              id="instTelefone"
              value={instituicao.telefone || ''}
              readOnly
            />
          </FormItem>
          <FormItem>
            <Label htmlFor="instEmail">Email</Label>
            <Input
              type="email"
              id="instEmail"
              value={instituicao.email || ''}
              readOnly
            />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem>
            <Label htmlFor="reitorDiretor">Reitor/Diretor</Label>
            <Input
              type="text"
              id="reitorDiretor"
              value={instituicao.reitorDiretor || ''}
              readOnly
            />
          </FormItem>
          <FormItem>
            <Label htmlFor="emailReitorDiretor">Email (Reitor/Diretor)</Label>
            <Input
              type="email"
              id="emailReitorDiretor"
              value={instituicao.emailReitorDiretor || ''}
              readOnly
            />
          </FormItem>
        </FormRow>

        <FileSection>
          <FileLabel>Arquivo: Termo de Cooperação Técnica</FileLabel>
          <FileContainer>
            <FileName>{termo?.nome || 'Nenhum termo encontrado'}</FileName>
            {userData && termo && (
              <DownloadButton
                type="button"
                onClick={handleDownloadTermo}
                disabled={downloadingTermo}
              >
                {downloadingTermo ? 'Baixando...' : 'Baixar 📎'}
              </DownloadButton>
            )}
          </FileContainer>
        </FileSection>
      </>
    );
  };

  const renderCursos = () => {
    const aguardandoAprovacao = user?.cargoId === CARGO_COORDENADOR_ESTAGIO
      && user?.status !== 'ATIVO';

    if (!cursos || cursos.length === 0) {
      return (
        <>
          <SectionTitle>Dados do Curso</SectionTitle>
          <ErrorMessage>
            {aguardandoAprovacao
              ? 'Nenhum curso ativo foi identificado para o CPF informado no cadastro.'
              : 'Nenhum curso vinculado encontrado.'}
          </ErrorMessage>
        </>
      );
    }
    return (
      <>
        <SectionTitle>Dados do Curso</SectionTitle>
        {cursos.some((curso) => curso.vinculoPendente) && (
          <ErrorMessage>
            Curso(s) identificado(s) pelo CPF informado. O vínculo oficial com a instituição será realizado após a aprovação do usuário.
          </ErrorMessage>
        )}
        {cursos.map((curso, idx) => (
          <div
            key={curso.id || idx}
            style={{
              marginBottom: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <FormRow>
              <FormItem>
                <Label htmlFor={`nomeRegistroMEC-${idx}`}>Nome de registro no MEC</Label>
                <Input
                  type="text"
                  id={`nomeRegistroMEC-${idx}`}
                  value={curso.nomeRegistroMEC ?? ''}
                  readOnly
                />
              </FormItem>
              <FormItem>
                <Label htmlFor={`sigla-${idx}`}>Sigla</Label>
                <Input
                  type="text"
                  id={`sigla-${idx}`}
                  value={curso.sigla ?? ''}
                  readOnly
                />
              </FormItem>
            </FormRow>
            <FormRow>
              <FormItem>
                <Label htmlFor={`campus-${idx}`}>Campus</Label>
                <Input
                  type="text"
                  id={`campus-${idx}`}
                  value={curso.campus || ''}
                  readOnly
                />
              </FormItem>
              <FormItem>
                <Label htmlFor={`curso-${idx}`}>Curso</Label>
                <Input
                  type="text"
                  id={`curso-${idx}`}
                  value={curso.curso || ''}
                  readOnly
                />
              </FormItem>
            </FormRow>
          </div>
        ))}
      </>
    );
  };

  const renderEstabelecimento = () => {
    if (!estabelecimento) {
      return (
        <>
          <SectionTitle>Dados do Estabelecimento de Saúde</SectionTitle>
          <ErrorMessage>Nenhum estabelecimento de saúde vinculado encontrado.</ErrorMessage>
        </>
      );
    }
    return (
      <>
        <SectionTitle>Dados do Estabelecimento de Saúde</SectionTitle>
        {user?.status !== 'ATIVO' && (
          <ErrorMessage>Estabelecimento de saúde selecionado</ErrorMessage>
        )}
        <FormRow>
          <FormItem>
            <Label htmlFor="estabNome">Nome</Label>
            <Input
              type="text"
              id="estabNome"
              value={estabelecimento.nome || ''}
              readOnly
            />
          </FormItem>
          <FormItem>
            <Label htmlFor="estabTipo">Tipo</Label>
            <Input
              type="text"
              id="estabTipo"
              value={estabelecimento.tipo || ''}
              readOnly
            />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem>
            <Label htmlFor="nomeDiretor">Diretor</Label>
            <Input
              type="text"
              id="nomeDiretor"
              value={estabelecimento.nomeDiretor || ''}
              readOnly
            />
          </FormItem>
          <FormItem>
            <Label htmlFor="cargoDiretor">Cargo do Diretor</Label>
            <Input
              type="text"
              id="cargoDiretor"
              value={estabelecimento.cargoDiretor || ''}
              readOnly
            />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem>
            <Label htmlFor="nomeDiretorTecnico">Diretor Técnico</Label>
            <Input
              type="text"
              id="nomeDiretorTecnico"
              value={estabelecimento.nomeDiretorTecnico || ''}
              readOnly
            />
          </FormItem>
          <FormItem>
            <Label htmlFor="cargoDiretorTecnico">Cargo do Diretor Técnico</Label>
            <Input
              type="text"
              id="cargoDiretorTecnico"
              value={estabelecimento.cargoDiretorTecnico || ''}
              readOnly
            />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem>
            <Label htmlFor="nomeResponsavelTecnico">Responsável Técnico</Label>
            <Input
              type="text"
              id="nomeResponsavelTecnico"
              value={estabelecimento.nomeResponsavelTecnico || ''}
              readOnly
            />
          </FormItem>
          <FormItem>
            <Label htmlFor="cargoResponsavelTecnico">Cargo do Responsável Técnico</Label>
            <Input
              type="text"
              id="cargoResponsavelTecnico"
              value={estabelecimento.cargoResponsavelTecnico || ''}
              readOnly
            />
          </FormItem>
        </FormRow>
      </>
    );
  };

  const renderCargoEspecifico = () => {
    switch (user?.cargoId) {
      case CARGO_INSTITUICAO:
        return renderInstituicao();
      case CARGO_COORDENADOR_ESTAGIO:
        return renderCursos();
      case CARGO_NEPS_GEPS:
        return renderEstabelecimento();
      default:
        return null;
    }
  };

  return (
    <Container>
      {editable && (
        <HeaderRow>
          <EditButton
            type="button"
            onClick={handleEditToggle}
            disabled={saving}
            title={isEditing ? 'Cancelar edição' : 'Editar dados'}
          >
            <img src={editIcon} alt="Editar" width="22" />
            {isEditing ? 'Cancelar' : 'Editar'}
          </EditButton>
        </HeaderRow>
      )}

      <AvatarWrapper>
        <Avatar>
          <AvatarImage src={profileIcon} alt="Foto de perfil" />
        </Avatar>
        {user?.nome && <UserName>{user.nome}</UserName>}
        {user?.cargo && <CargoBadge>{user.cargo}</CargoBadge>}
      </AvatarWrapper>

      <FormWrapper>
        <SectionTitle>Dados Básicos</SectionTitle>
        <FormRow>
          <FormItem>
            <Label htmlFor="nome">Nome*</Label>
            <Input
              type="text"
              id="nome"
              name="nome"
              value={isEditing ? formData.nome : (user?.nome || '')}
              onChange={isEditing ? handleFieldChange('nome') : undefined}
              readOnly={!isEditing}
            />
          </FormItem>
          <FormItem>
            <Label htmlFor="cpf">CPF*</Label>
            <Input
              type="text"
              id="cpf"
              name="cpf"
              value={displayCpf(user?.cpf)}
              readOnly
            />
          </FormItem>
        </FormRow>

        <FormRow>
          <FormItem>
            <Label htmlFor="email">Email*</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={user?.email || ''}
              readOnly
            />
          </FormItem>
        </FormRow>

        <FormRow>
          <FormItem>
            <Label htmlFor="telefone">Telefone</Label>
            <Input
              type="text"
              id="telefone"
              name="telefone"
              inputMode="tel"
              autoComplete="tel"
              placeholder={isEditing ? '(00) 00000-0000' : ''}
              value={isEditing ? formData.telefone : displayTelefone(user?.telefone)}
              onChange={isEditing ? handleFieldChange('telefone') : undefined}
              readOnly={!isEditing}
              aria-invalid={!!telefoneErro || undefined}
              style={telefoneErro ? { borderColor: '#c0392b' } : undefined}
            />
            {telefoneErro && (
              <ErrorMessage>{telefoneErro}</ErrorMessage>
            )}
          </FormItem>
        </FormRow>

        {renderCargoEspecifico()}

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {editable && isEditing && (
          <ActionsRow>
            <CancelButton type="button" onClick={handleEditToggle} disabled={saving}>
              Cancelar
            </CancelButton>
            <SaveButton
              type="button"
              onClick={handleSave}
              disabled={saving || !!telefoneErro}
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </SaveButton>
          </ActionsRow>
        )}
      </FormWrapper>
    </Container>
  );
};

export default ProfileUserInfo;
